import React from 'react'
import { observer } from 'mobx-react'
import Spinner from '../../../components/Spinner'
import JobNavigation from '../../components/JobNavigation'
import link from '../../../infra/link'
import { Id } from '../../../types/definitions'
import styles from './CallCenter.sass'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/Card'
import Button from '../../../components/Button'
import CallCenterJobViewModel from '../JobSection/CallCenterJobViewModel'
import AddCouponDialog from '../AddCouponDialog'
import { useActivation } from '../../../hooks/mobx-hooks'
import Icon from '../../../components/Icon'
import JobCheckoutViewModel, {
  AppliedCouponViewModel,
} from '../../../jobs/views/JobRoot/Checkout/JobCheckoutViewModel'
import { Task } from 'mobx-task'
import { formatMoney } from '../../../utils/money-util'

interface JobPricingSectionProps {
  jobVm: CallCenterJobViewModel
}

export default observer(function JobCheckout({
  jobVm,
}: JobPricingSectionProps) {
  const vm = jobVm.jobCheckoutViewModel

  if (vm.fetchJobInfo.pending || !vm.job.id) {
    return (
      <div>
        <Spinner size={20} />
      </div>
    )
  }

  return (
    <>
      <Card>
        <div className={styles.content}>
          <CardHeader>
            <CardTitle>$ Pricing</CardTitle>
          </CardHeader>

          <CardContent>
            <div className={styles.pricing}>
              <div className={styles.total}>{formatMoney(vm.amountDueNow)}</div>
              <div>Amount due now</div>
            </div>

            <PriceBreakdown jobVm={jobVm} checkoutVm={vm} />
          </CardContent>

          <div className={styles.button}>
            <Button size="cta" className={styles.button} type="primary">
              Generate Payment Link
            </Button>
          </div>
        </div>
      </Card>

      <JobNavigation previous={link.callCenterJob(Id.assert(vm.id))} />

      <AddCouponDialog vm={jobVm.addCouponViewModel} />
    </>
  )
})

interface PriceBreakdownProps {
  jobVm: CallCenterJobViewModel
  checkoutVm: JobCheckoutViewModel
}

const PriceBreakdown = observer(function PriceBreakDown({
  checkoutVm,
  jobVm,
}: PriceBreakdownProps) {
  return (
    <div className={styles.breakdown}>
      <b>Price Breakdown</b>
      <div className={styles.items}>
        {checkoutVm.jobScopeItems.map((item) => (
          <div key={item.id} className={styles.item}>
            <div>{item.name}</div>
            <div>{item.displayTotal}</div>
          </div>
        ))}
        {checkoutVm.hasCoupons ? (
          <>
            <div className={styles.item}>
              <div>Discount coupon</div>
              <div>{checkoutVm.discount}</div>
            </div>

            {checkoutVm.appliedCouponVM && (
              <CouponDetail
                vm={checkoutVm.appliedCouponVM}
                remove={jobVm.removeCoupon}
              />
            )}
          </>
        ) : (
          <div>
            <Button
              className={styles.button}
              type="text"
              onClick={() => jobVm.addCouponViewModel.show()}
            >
              Add Coupon
            </Button>
          </div>
        )}
      </div>
      <div className={styles.item}>
        <div>Total</div>
        <div>{checkoutVm.total}</div>
      </div>
    </div>
  )
})

interface CouponDetailProps {
  vm: AppliedCouponViewModel
  remove: Task<[], Promise<void>>
}

const CouponDetail = observer(function CouponDetail({
  vm,
  remove,
}: CouponDetailProps) {
  useActivation(vm)

  return (
    <div className={styles.couponSummary}>
      <div className={styles.appliedBadge}>
        <Icon size={12} name="tick" /> Applied
      </div>
      <div>
        <div>{vm.name}</div>
        <div>{vm.description}</div>
      </div>
      <div>{vm.discount}</div>
      <Button
        loading={remove.pending}
        type="danger"
        borderless
        onClick={remove}
      >
        <Icon size={24} name="close" />
      </Button>
    </div>
  )
})
